import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import "../assets/css/Home.css";

import heroFallback from "../assets/images/hero-bg.jpg";
import heroMobFallback from "../assets/images/home-hero-bg.jpeg";
import coupleCar from "../assets/images/couple-car.jpg";
import indiaMap from "../assets/images/india-map.png";
import uaeMap from "../assets/images/uae-map.png";

/* Brand icons */
import jaguar from "../assets/icons/jaguar.png";
import mini from "../assets/icons/mini-cooper.png";
import audi from "../assets/icons/audi-circle.png";
import ferrari from "../assets/icons/ferrari.png";
import toyota from "../assets/icons/toyota.png";
import hyundai from "../assets/icons/hyundai.png";

/* Feature icons */
import carIcon from "../assets/icons/la_car-side.png";
import checklistIcon from "../assets/icons/octicon_checklist-24.png";
import priceIcon from "../assets/icons/ion_pricetag-outline.png";

const Home = () => {
  const { t } = useTranslation();

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  /* Detect screen size */
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const heroBgImage = isMobile ? heroMobFallback : heroFallback;

  return (
    <main className="home">
      {/* HERO */}
      <section
        className="hero"
        style={{ backgroundImage: `url(${heroBgImage})` }}
      >
        <div className="hero-overlay">
          <div className="container new-container">
            <div className="container-in-pd">
              <h1>
                <Trans
                  i18nKey="home.hero.fallbackLine1"
                  components={{
                    span: <span className="title-highlight" />,
                    br: <br />
                  }}
                />
              </h1>

              <p className="hero-subtext">
                <Trans i18nKey="home.hero.fallbackSubtitle" />
              </p>

              <div className="hero-buttons">
                <Link to="/contact" className="btn me-3 c-btn">
                  {t("home.hero.primaryCta")}
                </Link>

                <Link to="/about#what-we-offer" className="btn secondary line-btn">
                  {t("home.hero.secondaryCta")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BRANDS */}
      <section className="brand-strip">
        <div className="container">
          <div className="container-in-pd">
            <div className="row text-center align-items-center c-no-wrap">
                <div className="col m-brands">
                  <img src={audi} alt="brand" />
                </div>
                <div className="col m-brands">
                  <img src={ferrari} className="s-car-icon" alt="brand" />
                </div>
                <div className="col m-brands">
                  <img src={hyundai} alt="brand" />
                </div>
                <div className="col m-brands">
                  <img src={jaguar} alt="brand" />
                </div>
                <div className="col m-brands">
                  <img src={toyota} className="s-car-icon" alt="brand" />
                </div>
                <div className="col m-brands">
                  <img src={mini} alt="brand" />
                </div>
            </div>
          </div>
        </div>
      </section>

      {/* CLARITY */}
      <section className="clarity">
        <div className="container text-center">
          <div className="container-in-pd">
            <h2 className="mb-5">{t("home.clarity.title")}</h2>
            <p className="clarity-p mb-3">{ <Trans i18nKey="home.clarity.text" />}</p>

            <Link to="/contact" className="link-btn mt-3">
              {t("home.clarity.link")} &gt;
            </Link>
          </div>
        </div>
      </section>

      {/* FEATURES BG */}
      <section
        className="features-bg"
        style={{ backgroundImage: `url(${coupleCar})` }}
      />

      {/* FEATURES */}
      <section>
        <div className="features-text-section">
          <div className="container new-container">
            <div className="container-in-pd">
              <div className="row g-4">
                {t("home.features", { returnObjects: true }).map((item, i) => (
                  <div className="col-md-4" key={i}>
                    <div className="ex-padding">
                      <div className="feature-card">
                        <img
                          src={[carIcon, priceIcon, checklistIcon][i]}
                          alt=""
                        />
                        <h3>{item.title}</h3>
                        <p>{item.text}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PAN INDIA */}
      <section className="pan-india">
        <div className="container new-container">
          <div className="container-in-pd new-container">
            <div className="row align-items-center mb-5">
              <div className="col-md-7">
                <h2>
                  {t("home.panIndia.title1")} <br />
                  {t("home.panIndia.title2")}
                </h2>
                <p>{t("home.panIndia.text")}</p>
              </div>
              <div className="col-md-5 text-center">
                <img src={indiaMap} alt="India map" className="india-map" />
              </div>
            </div>

            <div className="row g-4">
              {t("home.panIndia.items", { returnObjects: true }).map((item, i) => (
                <div className="col-md-4" key={i}>
                  <div className="ex-padding">
                    <h4>{item.title}</h4>
                    <p className="panindia-items-p">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* UAE */}
      <section className="uae-section">
        <div className="uae-sub-section">
          <div className="container text-center new-container">
            <div className="uae-backdrop">
              <img
                src={uaeMap}
                alt="UAE Map"
                className="uae-map-img my-4"
              />
              <h2 className="mb-3 fw-6"><Trans i18nKey="home.uae.title" /></h2>
              <p className="mb-3">{t("home.uae.text")}</p>


            </div>

            <Link to="/contact#contact-form" className="btn c-btn mt-3">
              {t("home.uae.cta")}
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="cta">
        <div className="container text-center">
          <h2>{t("home.cta.title")}</h2>
          <p>{t("home.cta.text")}</p>

          <Link to="/contact" className="btn c-btn mt-3">
            {t("home.cta.button")}
          </Link>
        </div>
      </section>
    </main>
  );
};

export default Home;
